/**
 * LA document type names and codes for proposal uploads.
 * Source: business mapping (document category + LA code).
 *
 * Extend `*_BY_NORMALIZED_LABEL` when API `documentName` values differ — keys are
 * lowercase, trimmed, single-spaced (see `normalizeDocumentLabel`).
 */

export const LA_DOCUMENT_TYPE = {
  INCOME_PROOF: "LA_IncomeProof",
  BANK_PROOF: "LA_BankProof",
  SURROGATE_PROOF: "LA_SurrogateProof",
  COMPANY_ID_FRONT: "LA_CompanyIDFront",
  COMPANY_ID_BACK: "LA_CompanyIDBack",
  IDENTIFICATION_PROOF: "LA_IndentificationProof",
  ADDRESS_PROOF: "LA_AddressProof",
  AGE_PROOF: "LA_AgeProof",
  PHOTOGRAPH: "LA_PHOTO",
  MEDICAL_DOC: "LA_MedicalDoc",
  FATCA_DECLARATION: "LA_FATCA",
};

/** Income proof: proposal dropdown stores `value` codes (see getIncomeProofOptions). */
export const INCOME_PROOF_LA_BY_LABEL = {
  "itr for last 2 years": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "INC2YRTN",
  },
  "last 6 months bank statements": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "BNK STMT",
  },
  "salary slips for last 3 months": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "SALAL3MN",
  },
  "appointment letter / salary revision letter": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "LATAPPLT",
  },
  "form 16a / form 16 / form 26as": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "LASYF16A",
  },
  "itr for last 3 years": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "INCM TAX",
  },
  "audited profit & loss account and audited balance sheet for last 2 years": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "AUPLBST2",
  },
  "audited profit & loss account and audited balance sheet for last 3 years": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "OTHERS",
  },
};

const BANK_PROOF_LA_MAP = {
  "Cancelled Cheque": {
    documentType: LA_DOCUMENT_TYPE.BANK_PROOF,
    documentTypeCode: "CANCELLED_CHQ",
  },
  Passbook: {
    documentType: LA_DOCUMENT_TYPE.BANK_PROOF,
    documentTypeCode: "PASSBOOK",
  },
  "Bank Statement": {
    documentType: LA_DOCUMENT_TYPE.BANK_PROOF,
    documentTypeCode: "BANK STM",
  },
};

export const SURROGATE_INCOME_PROOF_LA_BY_LABEL = {
  "car ownership (registration + latest insurance)": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "CARRC",
  },
  "mutual fund sip statement (last 12 months)": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "MFSIP",
  },
  "credit card statement (last 6 months)": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "CREDITC",
  },
  "12-month bank statement": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "BNKSTMT",
  },
  "investment portfolio (fd/portfolio statement)": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "INVFDPS",
  },
  "home loan/property loan emi statement": {
    documentType: LA_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "HOMELOAN",
  },
};

/** Photograph: PHOTOGRAPH_OPTIONS — both map to LA_PHOTO. */
export const PHOTOGRAPH_LA_BY_LABEL = {
  "passport photo": {
    documentType: LA_DOCUMENT_TYPE.PHOTOGRAPH,
    documentTypeCode: "LA_PHOTO",
  },
  selfie: {
    documentType: LA_DOCUMENT_TYPE.PHOTOGRAPH,
    documentTypeCode: "LA_PHOTO",
  },
};

/**
 * Address proof: keys match API `documentName` after normalization.
 * Add or adjust keys if masters return different wording.
 */
export const ADDRESS_PROOF_LA_BY_LABEL = {
  "driving license": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "DRIV LCN",
  },
  "driving licence": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "DRIV LCN",
  },
  "telephone bill": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "TEL BILL",
  },
  "bank statement": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "BANK STM",
  },
  "electricity bill": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "ELCT BIL",
  },
  "lease agreement": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "LEASE AG",
  },
  "lease agreement & rent receipt": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "LEASE AG",
  },
  "copy registered lease agreement with rent receipt (recent)/bankers certification": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "LEASEAGM",
  },
  "telephone/ mobile bill (2 months)": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "TEL BILL",
  },
  "mobile bill": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "TEL BILL",
  },
  "bank account statement (3 months)": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "BANK STM",
  },
  "bihar rajya parichay patra": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "BIHAR",
  },
  passport: {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "PASSPORT",
  },
  "voter's id": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "VT ID CD",
  },
  aadhaar: {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "AADHAR",
  },
  bihar: {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "BIHAR",
  },
  "domicile certificate": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "DOMICILE",
  },
  "insurance policy": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "INSUPOLI",
  },
  "letter from sem/pa*": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "SEM/GO/P",
  },
  "consumer gas connection card/ book/ pipe gas bill (2 months)": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "GASCARD",
  },
  "pension payment": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "PENSIONP",
  },
  "banker's certification": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "BANK CER",
  },
  "certificate by village extension officer (veo)/ village head/ equivalent rank officer": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "VEOCERT",
  },
  "post office savings pass book": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "POPASS",
  },
  "certificate from ward officer/ equivalent rank officer maintaining election roll certifying address of applicant": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "CERTWARD",
  },
  "letter of allotment of accommodation from employer issued by state or central government departments, statutory or regulatory bodies, public sector undertakings, scheduled commercial banks, financial institutions and listed companies": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "ALLOTLTR",
  },
  "kra kyc records": {
    documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "OTHERS",
  },
};

/** Identification proof — keys normalized from `documentName`. */
export const IDENTIFICATION_PROOF_LA_BY_LABEL = {
  "passport-valid": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "PASSPORT",
  },
  "identity card issued in north east & bihar by the state government": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "NEBIHAR",
  },
  "pan card": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "PAN CARD",
  },
  "photograph certified by an insurance company employee (ice)": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "CERT PIC",
  },
  bihar: {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "BIHAR",
  },
  "bihar rajya pehchan patra": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "BIHAR",
  },
  "employee id card": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "OTHERS",
  },
  "driving license": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "DRVR LIC",
  },
  "driving licence": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "DRVR LIC",
  },
  aadhaar: {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "AADHAR",
  },
  "employer certification": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "CERT EMP",
  },
  "bank certification": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "BANK CER",
  },
  "defence id card": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "OTHERS",
  },
  "Employee ID Card": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "OTHERS",
  },
  "voters identity card": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "VOTER IC",
  },
  "card issued by national rural employment guarantee act (nrega)": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "NREGACRD",
  },
  "kra kyc records": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "OTHERS",
  },
  "*sem/go/pa verification": {
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "PBL CERT",
  },
};

/**
 * Age proof → BizOps LA_AgeProof codes (SD/ND/NU/SA/SI/SP/NP).
 * Default SD (driving on/after 18.04.1993) and SP (passport on/after 01.04.1986); journey may send issue dates to refine ND/NP.
 */
export const AGE_PROOF_LA_BY_LABEL = {
  "driving licence": { documentType: LA_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "SD" },
  "aadhar card with date of birth": { documentType: LA_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "NU" },
  "army identity card": { documentType: LA_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "SA" },
  "pan card": { documentType: LA_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "SI" },
  passport: { documentType: LA_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "SP" },
};

/** Company ID card slot when employer is not "Other" — confirm code with integration. */
export const COMPANY_ID_CARD_LA = {
  documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
  documentTypeCode: "ICE",
};

export const COMPANY_ID_FRONT_LA = {
  documentType: LA_DOCUMENT_TYPE.COMPANY_ID_FRONT,
  documentTypeCode: "Company ID (Front)",
};

export const COMPANY_ID_BACK_LA = {
  documentType: LA_DOCUMENT_TYPE.COMPANY_ID_BACK,
  documentTypeCode: "Company ID (Back)",
};

/**
 * Last-resort LA payload by upload slot when selection context is unavailable
 * (e.g. review screen before selections are persisted).
 */
export const FALLBACK_LA_BY_SLOT = {
  photograph: { documentTypeCode: "LA_PHOTO", documentType: LA_DOCUMENT_TYPE.PHOTOGRAPH },
  ageProof: { documentTypeCode: "NU", documentType: LA_DOCUMENT_TYPE.AGE_PROOF },
  identificationProof: {
    documentTypeCode: "PAN CARD",
    documentType: LA_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
  },
  addressProof: { documentTypeCode: "BANK STM", documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF },
  addressProofPermanent: { documentTypeCode: "BANK STM", documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF },
  addressProofCommunication: { documentTypeCode: "BANK STM", documentType: LA_DOCUMENT_TYPE.ADDRESS_PROOF },
  incomeProof: { documentTypeCode: "BNK STMT", documentType: LA_DOCUMENT_TYPE.INCOME_PROOF },
  bankAccountProof: { documentTypeCode: "BANK STM", documentType: LA_DOCUMENT_TYPE.BANK_PROOF},
  surrogateIncomeProof: { documentTypeCode: "BNKSTMT", documentType: LA_DOCUMENT_TYPE.INCOME_PROOF },
  medicalDoc: { documentTypeCode: "LA_Medical", documentType: LA_DOCUMENT_TYPE.MEDICAL_DOC },
};

const CACHE_KEYS = {
  addressProof: "addressdocumentproof",
  ageProof: "agedocumentproof",
  identificationProof: "identificationdocumentproof",
};

export function normalizeDocumentLabel(value) {
  if (value == null || String(value).trim() === "") return "";
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Canonical values persisted in journey `ageProof` (must match BizOpsAgeProofMaster label mapping). */
export const CANONICAL_AGE_PROOF_LABEL = {
  DRIVING_LICENCE: "Driving Licence",
  AADHAR_DOB: "Aadhar Card with Date of Birth",
  ARMY: "Army Identity Card",
  PAN: "PAN Card",
  PASSPORT: "Passport",
};

/**
 * Maps API `documentName` (any casing) to one of five canonical age-proof labels for cumulative storage.
 */
export function toCanonicalAgeProofLabel(documentName) {
  if (documentName == null || String(documentName).trim() === "") return "";
  const n = normalizeDocumentLabel(documentName);
  if (!n) return String(documentName).trim();
  if (n.includes("driving") || (n.includes("drive") && n.includes("licen"))) {
    return CANONICAL_AGE_PROOF_LABEL.DRIVING_LICENCE;
  }
  if (n.includes("aadhar") || n.includes("aadhaar")) {
    return CANONICAL_AGE_PROOF_LABEL.AADHAR_DOB;
  }
  if (n.includes("army")) {
    return CANONICAL_AGE_PROOF_LABEL.ARMY;
  }
  if (n.includes("pan")) {
    return CANONICAL_AGE_PROOF_LABEL.PAN;
  }
  if (n.includes("passport")) {
    return CANONICAL_AGE_PROOF_LABEL.PASSPORT;
  }
  return String(documentName).trim();
}

function lookupByNormalizedMap(map, label) {
  const key = normalizeDocumentLabel(label);
  if (!key) return null;
  if (map[key]) return map[key];
  for (const [k, v] of Object.entries(map)) {
    if (key.includes(k) || k.includes(key)) return v;
  }
  return null;
}

/** Match proposal dropdown codes (e.g. INC2YRTN) to LA payloads. */
function lookupLaPayloadByDocumentTypeCode(map, code) {
  const key = normalizeDocumentLabel(code);
  if (!key) return null;
  for (const payload of Object.values(map)) {
    if (normalizeDocumentLabel(payload.documentTypeCode) === key) {
      return payload;
    }
  }
  return null;
}

function resolveIncomeProofLaPayload(selection) {
  return (
    lookupByNormalizedMap(INCOME_PROOF_LA_BY_LABEL, selection) ||
    lookupLaPayloadByDocumentTypeCode(INCOME_PROOF_LA_BY_LABEL, selection)
  );
}

function resolveSurrogateIncomeProofLaPayload(selection) {
  return (
    lookupByNormalizedMap(SURROGATE_INCOME_PROOF_LA_BY_LABEL, selection) ||
    lookupLaPayloadByDocumentTypeCode(
      SURROGATE_INCOME_PROOF_LA_BY_LABEL,
      selection,
    )
  );
}

/**
 * @param {'incomeProof'|'addressProof'|'ageProof'|'identificationProof'|'photograph'} category
 * @param {string} selectionLabel — display name or form string
 * @returns {{ documentType: string, documentTypeCode: string } | null}
 */
export function getLaPayloadForCategory(category, selectionLabel) {
  const label = selectionLabel;
  switch (category) {
    case "photograph":
      return lookupByNormalizedMap(PHOTOGRAPH_LA_BY_LABEL, label);
    case "incomeProof":
      return resolveIncomeProofLaPayload(label);
    case "surrogateIncomeProof":
      return resolveSurrogateIncomeProofLaPayload(label);
    case "addressProof":
      return lookupByNormalizedMap(ADDRESS_PROOF_LA_BY_LABEL, label);
    case "ageProof":
      return lookupByNormalizedMap(AGE_PROOF_LA_BY_LABEL, label);
    case "identificationProof":
      return lookupByNormalizedMap(IDENTIFICATION_PROOF_LA_BY_LABEL, label);
    case "bankAccountProof":
      return lookupByNormalizedMap(BANK_PROOF_LA_MAP, label);
    case "medicalDoc":
      return { documentType: LA_DOCUMENT_TYPE.MEDICAL_DOC, documentTypeCode: "LA_Medical" };
    case "fatcaDeclaration":
      return { documentType: LA_DOCUMENT_TYPE.FATCA_DECLARATION, documentTypeCode: "FATCA" };
    case "companyIdBack":
      return { documentType: LA_DOCUMENT_TYPE.FATCA_DECLARATION, documentTypeCode: "FATCA" };
    case "companyIdFront":
      return { documentType: LA_DOCUMENT_TYPE.FATCA_DECLARATION, documentTypeCode: "FATCA" };
    default:
      return null;
  }
}

function masterDocumentName(field, formValue, apiCache) {
  const cacheKey = CACHE_KEYS[field];
  if (!cacheKey || formValue == null || formValue === "") return "";
  const list = apiCache[cacheKey]?.data || [];
  const row = list.find(
    (d) => String(d.documentID) === String(formValue),
  );
  return row?.documentName || "";
}

const SLOT_CATEGORY = {
  photograph: "photograph",
  addressProof: "addressProof",
  addressProofPermanent: "addressProof",
  addressProofCommunication: "addressProof",
  bankAccountProof: "bankAccountProof",
  ageProof: "ageProof",
  incomeProof: "incomeProof",
  surrogateIncomeProof: "surrogateIncomeProof",
  identificationProof: "identificationProof",
  medicalDoc: "medicalDoc",
  fatcaDeclaration: "fatcaDeclaration",
  companyIdFront: "identificationProof",
  companyIdBack: "identificationProof",
};

/**
 * Resolves LA `documentType` + `documentTypeCode` for an upload slot from
 * proposal form data and journey API cache.
 *
 * @param {string} slotId — e.g. addressProof, idProof
 * @param {{ formData: object, apiCache: object }} context
 */
export function resolveSlotLaPayload(slotId, context) {
  const { formData, apiCache = {} } = context;

  if (slotId === "idProof") {
    return COMPANY_ID_CARD_LA;
  }

  if (slotId === "companyIdFront") {
    return COMPANY_ID_FRONT_LA;
  }

  if (slotId === "companyIdBack") {
    return COMPANY_ID_BACK_LA;
  }

  if (slotId === "fatcaDeclaration") {
    return {
      documentType: LA_DOCUMENT_TYPE.FATCA_DECLARATION,
      documentTypeCode: "FATCA",
    };
  }

  const category = SLOT_CATEGORY[slotId];
  if (!category) return null;

  let selectionLabel = "";
  if (category === "photograph") {
    selectionLabel = formData.photograph;
  } else if (category === "incomeProof") {
    selectionLabel = formData.incomeProof;
  } else if (category === "bankAccountProof") {
    selectionLabel = formData.bankAccountProof;
  } else if (category === "surrogateIncomeProof") {
    const surrogatePayload = resolveSurrogateIncomeProofLaPayload(
      formData.surrogateIncomeProof,
    );
    if (surrogatePayload) {
      return {
        documentType: LA_DOCUMENT_TYPE.SURROGATE_PROOF,
        documentTypeCode: surrogatePayload.documentTypeCode,
      };
    }
    selectionLabel = formData.surrogateIncomeProof;
  } else if (category === "addressProof") {
    // Masters cache stores rows by `documentID`, but the form selection typically keeps `documentID`.
    // Convert `documentID` -> `documentName` so label->LA mappings can be resolved.
    // Slot IDs for address can differ based on "communication same as permanent".
    const selectedAddressDocId =
      slotId === "addressProof"
        ? formData.addressProof ||
          formData.addressProofPermanent ||
          formData.addressProofCommunication
        : slotId === "addressProofPermanent"
        ? formData.addressProofPermanent
        : slotId === "addressProofCommunication"
          ? formData.addressProofCommunication
          : formData.addressProof;

    selectionLabel =
      masterDocumentName("addressProof", selectedAddressDocId, apiCache) ||
      selectedAddressDocId;
  } else if (category === "ageProof") {
    selectionLabel =
      masterDocumentName("ageProof", formData.ageProof, apiCache) ||
      formData.ageProof;
  } else if (category === "identificationProof") {
    selectionLabel =
      masterDocumentName(
        "identificationProof",
        formData.identificationProof,
        apiCache,
      ) || formData.identificationProof;
  }

  return getLaPayloadForCategory(category, selectionLabel);
}
